import { useCallback, useEffect, useRef, useState } from "react";
import { Outlet } from "react-router-dom";
import { Eye, LockKeyhole } from "lucide-react";
import { api } from "../api";
import { useAuth } from "../context/AuthContext";
import { RouteSeo } from "./Seo";
import UnderConstruction from "../pages/UnderConstruction";

export const SITE_PREVIEW_KEY = "ls_private_site_preview";

const CLOSED_FALLBACK = {
  is_live: false,
  mode: "construction",
  scheduled_at: null,
  headline: "A new chapter is taking shape.",
  message: "Our digital atelier is being prepared with care. We look forward to welcoming you soon.",
};

function readPreviewRequest() {
  try {
    return sessionStorage.getItem(SITE_PREVIEW_KEY) === "1";
  } catch {
    return false;
  }
}

function PrivatePreviewBanner({ onExit, client = false }) {
  return (
    <div className="private-preview-banner" role="status" data-testid="private-preview-banner">
      <span><LockKeyhole size={14} /> {client ? "Client preview" : "Private preview"}</span>
      <p>{client ? "Confidential pre-launch website preview. Public visitors still see Coming Soon." : "Only this signed-in admin session can see the website. Visitors still see Coming Soon."}</p>
      <button type="button" onClick={onExit}>
        Exit preview <Eye size={14} />
      </button>
    </div>
  );
}

export default function SiteGate() {
  const { user, isLoading: authLoading } = useAuth();
  const [status, setStatus] = useState(null);
  const [resolved, setResolved] = useState(false);
  const [previewRequested, setPreviewRequested] = useState(readPreviewRequest);
  const controllerRef = useRef(null);
  const requestIdRef = useRef(0);

  const refreshStatus = useCallback(async () => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;
    const timeout = window.setTimeout(() => controller.abort(), 6000);
    try {
      const { data } = await api.getOnce("/api/site-launch", {
        signal: controller.signal,
        cache: "no-store",
      });
      if (typeof data?.is_live !== "boolean") throw new Error("Invalid site status response");
      if (requestId !== requestIdRef.current) return;
      setStatus(data);
    } catch {
      if (requestId !== requestIdRef.current) return;
      // The first request fails closed. Later transient failures retain the
      // last server-confirmed state so a live checkout is never interrupted.
      setStatus((current) => current || CLOSED_FALLBACK);
    } finally {
      window.clearTimeout(timeout);
      if (requestId === requestIdRef.current) setResolved(true);
    }
  }, []);

  useEffect(() => {
    refreshStatus();
    const interval = window.setInterval(refreshStatus, 30000);
    return () => {
      window.clearInterval(interval);
      requestIdRef.current += 1;
      controllerRef.current?.abort();
    };
  }, [refreshStatus]);

  useEffect(() => {
    if (status?.mode !== "scheduled" || !status?.scheduled_at) return undefined;
    const serverNow = Date.parse(status.server_time || "") || Date.now();
    const launchAt = Date.parse(status.scheduled_at);
    const delay = Number.isFinite(launchAt) ? Math.max(500, launchAt - serverNow + 750) : 30000;
    const timer = window.setTimeout(refreshStatus, Math.min(delay, 2147483000));
    return () => window.clearTimeout(timer);
  }, [refreshStatus, status]);

  useEffect(() => {
    if (previewRequested && !authLoading && user?.role !== "admin") {
      try { sessionStorage.removeItem(SITE_PREVIEW_KEY); } catch {}
      setPreviewRequested(false);
    }
  }, [authLoading, previewRequested, user]);

  const exitPreview = () => {
    try { sessionStorage.removeItem(SITE_PREVIEW_KEY); } catch {}
    setPreviewRequested(false);
    window.location.assign("/admin/launch");
  };

  const exitClientPreview = async () => {
    try { await api.post("/api/site-preview/logout", {}); } catch {}
    window.location.assign("/preview");
  };

  if (status?.is_live) {
    return <><RouteSeo /><Outlet /></>;
  }

  if (previewRequested && user?.role === "admin") {
    return (
      <div className="private-preview-active">
        <PrivatePreviewBanner onExit={exitPreview} />
        <RouteSeo />
        <Outlet />
      </div>
    );
  }

  if (status?.preview_authorized) {
    return (
      <div className="private-preview-active">
        <PrivatePreviewBanner onExit={exitClientPreview} client />
        <RouteSeo />
        <Outlet />
      </div>
    );
  }

  return <UnderConstruction status={status || CLOSED_FALLBACK} loading={!resolved || (previewRequested && authLoading)} />;
}
